import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';

import '../styles/userlist.css';
import '../styles/general.css';
import '../styles/usersBulkEdit.css';

import { useAuth } from '../core/AuthContext';
import { useSemester } from '../core/SemesterContext';
import { URL } from '../core/config';
import NavHeader from './NavHeader';
import Avatar from './Avatar';
import CustomAlert from './CustomAlert';
import CustomAlertWithButtons from './CustomAlertWithButtons';
import LoadingSpinner from './LoadingSpinner';
import useUsers from './hooks/useUsers';
import useDepartmentFilter from './hooks/useDepartmentFilter';
import useMembershipFilter from './hooks/useMembershipFilter';
import {
  getDepartmentColor,
  getDepartmentIcon,
  getDepartmentsForContext,
  extractDepartmentIds,
  getDepartmentId,
  userHasDepartment,
} from '../core/constants/departments';
import { useDepartments } from '../core/DepartmentsContext';
import { hasPermission } from '../core/utils/permissionUtils';

const usersURL = URL + '/users';
const semestersURL = URL + '/semesters';

const PROFESSIONAL_STATUS_VALUES = ['Student', 'Student-Worker', 'Employee', 'Unemployed', 'Other'];
const MEMBERSHIP_TYPE_VALUES = ['Newbie', 'Junior', 'Senior', 'Alumni', 'Intern', 'Unbound'];

// Pill colors for the membership-type select, one of the few enum columns
// worth color-coding at a glance while scanning the sheet.
const MEMBERSHIP_PILL_COLORS = {
  Newbie: { bg: '#e7e7e2', fg: '#3a3a36', border: '#3a3a36' },
  Junior: { bg: '#cfe3fb', fg: '#1c4c8c', border: '#1c4c8c' },
  Senior: { bg: '#fbe3b0', fg: '#8a5a12', border: '#8a5a12' },
  Intern: { bg: '#c8f0e6', fg: '#0f6d57', border: '#0f6d57' },
  Alumni: { bg: '#e6d6f7', fg: '#5b21a6', border: '#5b21a6' },
  Unbound: { bg: '#fbdada', fg: '#9c1f1f', border: '#9c1f1f' },
};

// Same icon set as the membership filter on UsersList.jsx, kept in sync
// manually - plus Unbound, which that page's filter list omits.
const MEMBERSHIP_ICONS = {
  Newbie: <Icon icon="streamline:rock-and-roll-hand-remix" />,
  Junior: <Icon icon="bi:emoji-sunglasses" />,
  Senior: <Icon icon="mdi:emoticon-devil-outline" />,
  Intern: <Icon icon="mdi:school-outline" />,
  Alumni: <Icon icon="tdesign:wave-bye" />,
  Unbound: <Icon icon="mdi:account-off-outline" />,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatRoleNames(row) {
  return (row.roles || [])
    .map((role) => (typeof role === 'object' ? role.name : role))
    .filter(Boolean)
    .join(', ') || '—';
}

function buildFieldDefs(departmentOptions) {
  return [
  { key: 'fullName', label: 'Full Name', type: 'text', required: true },
  { key: 'firstLastName', label: 'First & Last Name', type: 'text', required: true },
  { key: 'nickname', label: 'Nickname', type: 'text', italic: true },
  { key: 'birthDate', label: 'Birth Date', type: 'date', required: true },
  { key: 'nationality', label: 'Nationality', type: 'text', required: true },
  { key: 'naturality', label: 'Naturality', type: 'text', required: true },
  { key: 'professionalStatus', label: 'Professional Status', type: 'select', required: true, pill: true, options: PROFESSIONAL_STATUS_VALUES.map((v) => ({ value: v, label: v })) },
  { key: 'personalEmail', label: 'Personal Email', type: 'email', required: true },
  { key: 'institutionalEmail', label: 'ESN Email', type: 'email' },
  { key: 'phone', label: 'Phone', type: 'text', required: true },
  { key: 'address', label: 'Address', type: 'text', required: true },
  { key: 'emergencyName', label: 'Emergency Contact Name', type: 'text', required: true },
  { key: 'emergencyRelationship', label: 'Emergency Relationship', type: 'text', required: true },
  { key: 'emergencyPhone', label: 'Emergency Phone', type: 'text', required: true },
  { key: 'mobilityPlace', label: 'Mobility Place', type: 'text' },
  { key: 'mobilityProgram', label: 'Mobility Program', type: 'text' },
  { key: 'dateJoined', label: 'Date Joined', type: 'date', required: true },
  { key: 'semesters', label: 'Semesters', type: 'multiselect', required: true },
  { key: 'membershipType', label: 'Membership Type', type: 'select', pill: true, colorMap: MEMBERSHIP_PILL_COLORS, options: MEMBERSHIP_TYPE_VALUES.map((v) => ({ value: v, label: v })) },
  { key: 'departments', label: 'Departments', type: 'multiselect', required: true, max: 2, options: departmentOptions },
  { key: 'roles', label: 'Role(s)', type: 'readonly', displayValue: formatRoleNames },
  { key: 'suspension.isSuspended', label: 'Suspended', type: 'checkbox' },
  { key: 'suspension.start', label: 'Suspension Start', type: 'date', showIf: (row) => row.suspension?.isSuspended },
  { key: 'suspension.end', label: 'Suspension End', type: 'date', showIf: (row) => row.suspension?.isSuspended },
  ];
}

// Fields that make sense to apply as a single value across many selected users at once.
const BATCHABLE_FIELDS = ['membershipType', 'departments', 'professionalStatus', 'suspension.isSuspended'];

// ─── helpers ────────────────────────────────────────────────────────────────

function getPath(obj, path) {
  return path.split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
}

function setPath(obj, path, value) {
  const keys = path.split('.');
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value };
  }
  const [head, ...rest] = keys;
  return { ...obj, [head]: setPath(obj[head] || {}, rest.join('.'), value) };
}

function isEmptyValue(value) {
  if (Array.isArray(value)) return value.length === 0;
  return value === undefined || value === null || String(value).trim() === '';
}

function valuesEqual(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());
  }
  return (a ?? '') === (b ?? '');
}

function toDateInputValue(value) {
  if (!value) return '';
  try {
    return format(new Date(value), 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

// Normalizes a user document fetched from the API into the shape the sheet
// edits: dates as yyyy-MM-dd strings, semesters as plain id strings, departments as ids.
function normalizeUser(user, catalog) {
  return {
    ...user,
    birthDate: toDateInputValue(user.birthDate),
    dateJoined: toDateInputValue(user.dateJoined),
    departments: extractDepartmentIds(user.departments, catalog),
    semesters: (user.semesters || []).map((s) => String(s?._id || s)),
    suspension: {
      isSuspended: !!user.suspension?.isSuspended,
      start: toDateInputValue(user.suspension?.start),
      end: toDateInputValue(user.suspension?.end),
    },
  };
}

// Mirrors EditProfile.jsx's own submit logic: suspension is always sent as a
// whole sub-object, never as a dotted path, since PATCH /users/:id spreads
// the body directly onto the document.
function buildPayload(row, dirtyKeys) {
  const payload = {};
  let includeSuspension = false;
  dirtyKeys.forEach((key) => {
    if (key.startsWith('suspension.')) {
      includeSuspension = true;
    } else {
      payload[key] = getPath(row, key);
    }
  });
  if (includeSuspension) {
    const isSuspended = !!row.suspension?.isSuspended;
    payload.suspension = {
      isSuspended,
      start: isSuspended ? (row.suspension?.start || null) : null,
      end: isSuspended ? (row.suspension?.end || null) : null,
    };
  }
  return payload;
}

function validateDirtyFields(row, dirtyKeys, fieldDefs) {
  const errors = [];
  dirtyKeys.forEach((key) => {
    const def = fieldDefs.find((f) => f.key === key);
    if (!def) return;
    const value = getPath(row, key);
    if (def.required && isEmptyValue(value)) {
      errors.push(`${def.label} is required`);
    }
    if (def.type === 'email' && !isEmptyValue(value) && !EMAIL_REGEX.test(value)) {
      errors.push(`${def.label} must be a valid email`);
    }
  });
  return errors;
}

// ─── cell components ────────────────────────────────────────────────────────

function useOutsideClick(ref, handler) {
  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) handler();
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [ref, handler]);
}

const TextCell = memo(({ value, onChange, disabled, type = 'text', italic }) => (
  <input
    type={type}
    className={`cell-input ${italic ? 'cell-input-italic' : ''}`}
    value={value || ''}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    onBlur={(e) => onChange(e.target.value.trim())}
  />
));

const DateCell = memo(({ value, onChange, disabled }) => (
  <input
    type="date"
    className="cell-input"
    value={value || ''}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
  />
));

const SelectCell = memo(({ value, options, onChange, disabled, pill, colorMap }) => {
  const isNumeric = typeof options?.[0]?.value === 'number';
  const swatch = colorMap?.[value];
  const style = pill && swatch
    ? { background: swatch.bg, color: swatch.fg, borderColor: swatch.border }
    : undefined;
  return (
    <select
      className={`cell-input cell-select ${pill ? 'cell-select-pill' : ''}`}
      style={style}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(isNumeric ? Number(e.target.value) : e.target.value)}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
});

const CheckboxCell = memo(({ value, onChange, disabled }) => (
  <input type="checkbox" checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
));

const ReadOnlyCell = memo(({ value }) => (
  <span className="cell-readonly">{value || '—'}</span>
));

const MultiSelectCell = memo(({ value = [], options = [], onChange, max, disabled, placeholder = 'None' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClick(ref, useCallback(() => setOpen(false), []));

  const labelFor = (v) => options.find((o) => o.value === v)?.label || v;
  const atMax = !!max && value.length >= max;

  const toggle = (optValue) => {
    if (disabled) return;
    if (value.includes(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      if (atMax) return;
      onChange([...value, optValue]);
    }
  };

  return (
    <div className="cell-multiselect" ref={ref}>
      <button type="button" className="cell-multiselect-trigger" disabled={disabled} onClick={() => setOpen((o) => !o)}>
        <span className="cell-multiselect-value">
          {value.length > 0 ? value.map(labelFor).join(', ') : placeholder}
        </span>
        <Icon icon="mdi:chevron-down" />
      </button>
      {open && !disabled && (
        <div className="cell-multiselect-popover">
          {options.map((opt) => {
            const checked = value.includes(opt.value);
            return (
              <label key={opt.value} className={`cell-multiselect-option ${!checked && atMax ? 'disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!checked && atMax}
                  onChange={() => toggle(opt.value)}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
});

// ─── row component ──────────────────────────────────────────────────────────

const UserRow = memo(function UserRow({
  row,
  dirty,
  status,
  errorMessage,
  selected,
  semesterOptions,
  fieldDefs,
  onToggleSelect,
  onFieldChange,
  onSave,
  onCancel,
}) {
  const isDirty = !!dirty && dirty.size > 0;

  return (
    <tr className={`${isDirty ? 'row-dirty' : ''} ${status === 'error' ? 'row-error' : ''} ${status === 'saved' ? 'row-saved' : ''}`}>
      <td className="sticky-col checkbox-col">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} />
      </td>
      <td className="sticky-col pic-col">
        <Avatar user={row} size={32} style={{ border: '2px solid #111' }} />
      </td>
      {fieldDefs.map((f) => {
        if (f.showIf && !f.showIf(row)) {
          return <td key={f.key} className="cell-empty" />;
        }
        const value = getPath(row, f.key);
        const isFieldDirty = dirty?.has(f.key);
        return (
          <td key={f.key} className={isFieldDirty ? 'cell-dirty' : ''}>
            {f.type === 'text' && (
              <TextCell value={value} italic={f.italic} onChange={(v) => onFieldChange(row._id, f.key, v)} />
            )}
            {f.type === 'email' && (
              <TextCell type="email" value={value} onChange={(v) => onFieldChange(row._id, f.key, v)} />
            )}
            {f.type === 'date' && (
              <DateCell value={value} onChange={(v) => onFieldChange(row._id, f.key, v)} />
            )}
            {f.type === 'select' && (
              <SelectCell
                value={value}
                options={f.options}
                pill={f.pill}
                colorMap={f.colorMap}
                onChange={(v) => onFieldChange(row._id, f.key, v)}
              />
            )}
            {f.type === 'multiselect' && (
              <MultiSelectCell
                value={value}
                options={f.key === 'semesters' ? semesterOptions : f.options}
                max={f.max}
                onChange={(v) => onFieldChange(row._id, f.key, v)}
              />
            )}
            {f.type === 'checkbox' && (
              <CheckboxCell value={value} onChange={(v) => onFieldChange(row._id, f.key, v)} />
            )}
            {f.type === 'readonly' && (
              <ReadOnlyCell value={f.displayValue ? f.displayValue(row) : value} />
            )}
          </td>
        );
      })}
      <td className="actions-col">
        {isDirty && (
          <div className="row-actions">
            <button type="button" className="row-save-btn" onClick={onSave} title="Save this row">
              <Icon icon="mdi:content-save" />
            </button>
            <button type="button" className="row-cancel-btn" onClick={onCancel} title="Discard changes">
              <Icon icon="mdi:undo" />
            </button>
          </div>
        )}
        {status === 'saving' && <Icon icon="mdi:loading" className="row-status-spin" />}
        {status === 'saved' && <Icon icon="mdi:check-circle" className="row-status-saved" />}
        {status === 'error' && (
          <span className="row-status-error" title={errorMessage}>
            <Icon icon="mdi:alert-circle" />
          </span>
        )}
      </td>
    </tr>
  );
});

// ─── main component ─────────────────────────────────────────────────────────

function UsersBulkEdit() {
  const authContext = useAuth();
  const contextUser = authContext.user;
  const semesterContext = useSemester();
  const semester = semesterContext.currentSemester;
  const { departments: catalog } = useDepartments();

  const filterDepartments = useMemo(
    () => getDepartmentsForContext('filters', catalog),
    [catalog]
  );
  const departmentOptions = useMemo(
    () =>
      getDepartmentsForContext('registration', catalog).map((d) => ({
        value: d._id ? String(d._id) : d.name,
        label: d.name,
      })),
    [catalog]
  );
  const fieldDefs = useMemo(
    () => buildFieldDefs(departmentOptions),
    [departmentOptions]
  );

  const { users, loading, error, semesterUsersLoaded, loadUnboundUsers } = useUsers();

  const canAccessPage = hasPermission(contextUser, 'MANAGE_USERS');

  const [semesterOptionsList, setSemesterOptionsList] = useState([]);

  const [usersById, setUsersById] = useState({});
  const [originalById, setOriginalById] = useState({});
  const [dirtyFields, setDirtyFields] = useState({});
  const [rowStatus, setRowStatus] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [savingAll, setSavingAll] = useState(false);
  const seededSemesterRef = useRef(null);
  const unboundIdsRef = useRef(new Set());
  const dirtyFieldsRef = useRef(dirtyFields);
  dirtyFieldsRef.current = dirtyFields;

  const [searchQuery, setSearchQuery] = useState('');
  const { selectedDepartments, handleDepartmentChange, setSelectedDepartments } = useDepartmentFilter();
  const { selectedMemberships, handleMembershipChange, setSelectedMemberships } = useMembershipFilter();

  const [batchField, setBatchField] = useState('membershipType');
  const [batchValue, setBatchValue] = useState('Newbie');
  const [confirmBatch, setConfirmBatch] = useState(false);
  const [applyingBatch, setApplyingBatch] = useState(false);

  const [alertInfo, setAlertInfo] = useState({ show: false, type: '', message: '' });
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  // Semesters list, needed for the "Semesters" multi-select column/batch value.
  useEffect(() => {
    if (!canAccessPage) return;
    let mounted = true;
    axios.get(`${semestersURL}/list`)
      .then((res) => { if (mounted) setSemesterOptionsList(res.data || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [canAccessPage]);

  const semesterFieldOptions = useMemo(
    () => semesterOptionsList.map((s) => ({ value: s._id, label: `${s.name}${s.isActive ? ' (Current)' : ''}` })),
    [semesterOptionsList]
  );

  // Seed local editable state once per semester load. Guarded so re-renders
  // (e.g. from an unrelated state update) never stomp in-progress edits.
  useEffect(() => {
    if (!semesterUsersLoaded) return;
    if (seededSemesterRef.current === semester?._id) return;
    const byId = {};
    users.forEach((u) => { byId[u._id] = normalizeUser(u, catalog); });
    setUsersById(byId);
    setOriginalById(byId);
    setDirtyFields({});
    setRowStatus({});
    setRowErrors({});
    setSelectedIds(new Set());
    seededSemesterRef.current = semester?._id;
  }, [semesterUsersLoaded, users, semester?._id, catalog]);

  const searchingUnbound = selectedMemberships.includes('Unbound');

  // Unbound members are absent from the default roster. Pull them in only
  // while that membership filter is on, and drop them again when it is cleared
  // unless a row still has unsaved edits.
  useEffect(() => {
    if (!semesterUsersLoaded || !semester?._id) return undefined;

    if (!searchingUnbound) {
      const ids = unboundIdsRef.current;
      if (ids.size === 0) return undefined;

      setUsersById((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (!dirtyFieldsRef.current[id]) delete next[id];
        });
        return next;
      });
      setOriginalById((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (!dirtyFieldsRef.current[id]) delete next[id];
        });
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => {
          if (!dirtyFieldsRef.current[id]) next.delete(id);
        });
        return next;
      });
      unboundIdsRef.current = new Set();
      return undefined;
    }

    let cancelled = false;
    loadUnboundUsers()
      .then((rows) => {
        if (cancelled) return;
        const ids = new Set(rows.map((user) => user._id));
        unboundIdsRef.current = ids;
        setUsersById((prev) => {
          const next = { ...prev };
          rows.forEach((user) => {
            if (!next[user._id]) next[user._id] = normalizeUser(user, catalog);
          });
          return next;
        });
        setOriginalById((prev) => {
          const next = { ...prev };
          rows.forEach((user) => {
            if (!next[user._id]) next[user._id] = normalizeUser(user, catalog);
          });
          return next;
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [searchingUnbound, semesterUsersLoaded, semester?._id, catalog, loadUnboundUsers]);

  const allRows = useMemo(() => Object.values(usersById), [usersById]);

  const departmentCounts = useMemo(() => {
    const counts = {};
    filterDepartments.forEach((dept) => {
      const key = getDepartmentId(dept) || dept.name;
      counts[key] = allRows.filter((u) => userHasDepartment(u.departments, key)).length;
    });
    return counts;
  }, [allRows, filterDepartments]);

  const membershipCounts = useMemo(() => {
    const counts = {};
    MEMBERSHIP_TYPE_VALUES.forEach((mem) => {
      counts[mem] = allRows.filter((u) => (u.membershipType || 'Newbie') === mem).length;
    });
    return counts;
  }, [allRows]);

  const filteredRows = useMemo(() => {
    const removeAccents = (str) => str?.normalize('NFD').replace(/\p{Diacritic}/gu, '') || '';
    const search = removeAccents(searchQuery).toLowerCase();
    return allRows
      .filter((u) => {
        const matchesSearch = !search ||
          removeAccents(u.fullName).toLowerCase().includes(search) ||
          removeAccents(u.nickname).toLowerCase().includes(search);
        const matchesDepartment = selectedDepartments.length === 0 ||
          selectedDepartments.some((filterDep) => userHasDepartment(u.departments, filterDep));
        const matchesMembership = selectedMemberships.length === 0 ||
          selectedMemberships.includes(u.membershipType);
        return matchesSearch && matchesDepartment && matchesMembership;
      })
      .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
  }, [allRows, searchQuery, selectedDepartments, selectedMemberships]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDepartments([]);
    setSelectedMemberships([]);
  };

  const handleFieldChange = useCallback((id, key, value) => {
    setUsersById((prev) => ({ ...prev, [id]: setPath(prev[id], key, value) }));
    setDirtyFields((prev) => {
      const original = getPath(originalById[id], key);
      const same = valuesEqual(value, original);
      const nextSet = new Set(prev[id] || []);
      if (same) nextSet.delete(key); else nextSet.add(key);
      const next = { ...prev };
      if (nextSet.size === 0) delete next[id]; else next[id] = nextSet;
      return next;
    });
    setRowStatus((prev) => (prev[id] ? { ...prev, [id]: null } : prev));
  }, [originalById]);

  const performSaveRow = useCallback(async (id) => {
    const dirty = dirtyFields[id];
    if (!dirty || dirty.size === 0) return { id, ok: true, skipped: true };
    const row = usersById[id];
    const errors = validateDirtyFields(row, dirty, fieldDefs);
    if (errors.length > 0) {
      setRowErrors((prev) => ({ ...prev, [id]: errors.join('; ') }));
      setRowStatus((prev) => ({ ...prev, [id]: 'error' }));
      return { id, ok: false, error: errors.join('; ') };
    }
    const payload = buildPayload(row, dirty);
    setRowStatus((prev) => ({ ...prev, [id]: 'saving' }));
    try {
      const { data } = await axios.patch(`${usersURL}/${id}`, payload);
      const normalized = normalizeUser(data, catalog);
      setUsersById((prev) => ({ ...prev, [id]: normalized }));
      setOriginalById((prev) => ({ ...prev, [id]: normalized }));
      setDirtyFields((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setRowStatus((prev) => ({ ...prev, [id]: 'saved' }));
      setTimeout(() => {
        setRowStatus((prev) => (prev[id] === 'saved' ? { ...prev, [id]: null } : prev));
      }, 2000);
      return { id, ok: true };
    } catch (err) {
      const message = err.response?.data?.error || err.message;
      setRowErrors((prev) => ({ ...prev, [id]: message }));
      setRowStatus((prev) => ({ ...prev, [id]: 'error' }));
      return { id, ok: false, error: message };
    }
  }, [dirtyFields, usersById, fieldDefs, catalog]);

  const handleCancelRow = useCallback((id) => {
    setUsersById((prev) => ({ ...prev, [id]: originalById[id] }));
    setDirtyFields((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setRowStatus((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, [originalById]);

  const dirtyIds = useMemo(() => Object.keys(dirtyFields).filter((id) => dirtyFields[id]?.size > 0), [dirtyFields]);

  const handleSaveAll = async () => {
    if (dirtyIds.length === 0) return;
    setSavingAll(true);
    const results = await Promise.all(dirtyIds.map((id) => performSaveRow(id)));
    setSavingAll(false);
    const succeeded = results.filter((r) => r.ok && !r.skipped).length;
    const failed = results.filter((r) => !r.ok);
    setAlertInfo({
      show: true,
      type: failed.length > 0 ? 'warning' : 'success',
      message: failed.length > 0
        ? `${succeeded} row(s) saved, ${failed.length} failed. Check the highlighted rows.`
        : `${succeeded} row${succeeded === 1 ? '' : 's'} saved successfully.`,
    });
  };

  // Warn on tab close/refresh if there are unsaved edits.
  useEffect(() => {
    const handler = (e) => {
      if (dirtyIds.length === 0) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirtyIds.length]);

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allFilteredSelected = filteredRows.length > 0 && filteredRows.every((r) => selectedIds.has(r._id));
  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredRows.forEach((r) => next.delete(r._id));
      } else {
        filteredRows.forEach((r) => next.add(r._id));
      }
      return next;
    });
  };

  const batchFieldDefs = useMemo(
    () => fieldDefs.filter((f) => BATCHABLE_FIELDS.includes(f.key)),
    [fieldDefs]
  );
  const activeBatchDef = batchFieldDefs.find((f) => f.key === batchField) || batchFieldDefs[0];

  const handleBatchFieldChange = (key) => {
    setBatchField(key);
    const def = batchFieldDefs.find((f) => f.key === key) || batchFieldDefs[0];
    if (!def) return;
    if (def.type === 'multiselect') setBatchValue([]);
    else if (def.type === 'checkbox') setBatchValue(false);
    else setBatchValue(def.options?.[0]?.value ?? '');
  };

  const requestBatchUpdate = () => {
    if (selectedIds.size === 0 || !activeBatchDef) return;
    setConfirmBatch(true);
  };

  const applyBatchUpdate = async () => {
    setConfirmBatch(false);
    setApplyingBatch(true);
    const ids = Array.from(selectedIds);
    try {
      const { data } = await axios.patch(`${usersURL}/bulk`, {
        ids,
        update: { [activeBatchDef.key]: batchValue },
      });
      const applyLocally = (prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (next[id]) next[id] = setPath(next[id], activeBatchDef.key, batchValue);
        });
        return next;
      };
      setUsersById(applyLocally);
      setOriginalById(applyLocally);
      setAlertInfo({
        show: true,
        type: 'success',
        message: `Updated "${activeBatchDef.label}" for ${data.modifiedCount} of ${data.matchedCount} selected user(s).`,
      });
    } catch (err) {
      setAlertInfo({
        show: true,
        type: 'error',
        message: err.response?.data?.error || err.message,
      });
    } finally {
      setApplyingBatch(false);
    }
  };

  if (!contextUser) return null;

  if (!canAccessPage) {
    return (
      <div className="page-container bulk-edit-root">
        <NavHeader />
        <div className="bulk-edit-no-permission">
          <Icon icon="mdi:lock-outline" width="2rem" height="2rem" />
          <p>You don't have permission to access the Member Tracking page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container bulk-edit-root">
      <NavHeader />

      <div className="page-title-row">
        <h1 className="page-title page-title-icon">
          <span className="main-section-icon">
            <span className="main-section-icon-fill" style={{ background: 'rgba(122, 193, 67, 0.15)', color: '#7ac143' }}>
              <Icon icon="mdi:table-edit" className="icon" />
            </span>
          </span>
          Member Tracking
        </h1>
        <div className="title-action-buttons">
          <Link to="/users-list" className="title-icon-button" aria-label="Back to Users List">
            <Icon icon="mdi:arrow-left" />
          </Link>
        </div>
      </div>

      <div className="user-list-container users-page bulk-edit-page">
        <div className={`user-list-lateral-section ${filtersCollapsed ? 'collapsed' : ''}`}>
          {filtersCollapsed ? (
            <button
              type="button"
              className="bulk-edit-filters-expand"
              onClick={() => setFiltersCollapsed(false)}
              aria-label="Expand filters"
            >
              <Icon icon="mdi:chevron-right" />
              <span className="bulk-edit-filters-expand-label">Filters</span>
            </button>
          ) : (
            <>
              <div className="bulk-edit-filters-header">
                <h2 className="bulk-edit-filters-title">Filters</h2>
                <button
                  type="button"
                  className="bulk-edit-filters-collapse"
                  onClick={() => setFiltersCollapsed(true)}
                  aria-label="Collapse filters"
                >
                  <Icon icon="mdi:chevron-left" />
                </button>
              </div>

              <div className="search-notification-layout" style={{ margin: '10px 0' }}>
                <div className="search-bar">
                  <input
                    type="text"
                    placeholder="Search ESNers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <i className="uil uil-search search-icon"></i>
                </div>
              </div>

              <div className="filter-container" style={{ margin: '10px 0' }}>
                <div className="filter-section" id="department-filter-section">
                  <h3 className="filter-title">Department</h3>
                  <div className="filters">
                    {filterDepartments.map((dept) => {
                      const filterValue = getDepartmentId(dept) || dept.name;
                      return (
                      <button
                        key={filterValue}
                        className={`filter-button ${selectedDepartments.includes(filterValue) ? 'selected' : ''}`}
                        onClick={() => handleDepartmentChange(filterValue)}
                        style={{ '--department-color': getDepartmentColor(dept, catalog) }}
                      >
                        <div className="department-icon">{getDepartmentIcon(dept, {}, catalog)}</div>
                        <span className="department-name">{dept.name}</span>
                        <span className="filter-count">{departmentCounts[filterValue] || 0}</span>
                      </button>
                      );
                    })}
                    {selectedDepartments.length > 0 && (
                      <button type="button" className="bulk-edit-filter-clear" onClick={() => setSelectedDepartments([])}>
                        <Icon icon="mdi:close" /> Clear
                      </button>
                    )}
                  </div>
                </div>

                <div className="filter-section" id="membership-filter-section">
                  <h3 className="filter-title">Membership</h3>
                  <div className="filters">
                    {MEMBERSHIP_TYPE_VALUES.map((mem) => (
                      <button
                        key={mem}
                        className={`filter-button ${selectedMemberships.includes(mem) ? 'selected' : ''}`}
                        onClick={() => handleMembershipChange(mem)}
                      >
                        <div className="membership-icon">{MEMBERSHIP_ICONS[mem]}</div>
                        <span className="membership-name">{mem}</span>
                        {(mem !== 'Unbound' || selectedMemberships.includes('Unbound')) && (
                          <span className="filter-count">{membershipCounts[mem] || 0}</span>
                        )}
                      </button>
                    ))}
                    {selectedMemberships.length > 0 && (
                      <button type="button" className="bulk-edit-filter-clear" onClick={() => setSelectedMemberships([])}>
                        <Icon icon="mdi:close" /> Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {(searchQuery || selectedDepartments.length > 0 || selectedMemberships.length > 0) && (
                <button className="button list-button clear-button" onClick={clearFilters}>
                  <Icon icon="material-symbols:clear-all" className="button-icon" />
                  Clear Filters
                </button>
              )}

              <div className="bulk-edit-batch-panel">
                <h3 className="filter-title">Batch Update</h3>
                <p className="bulk-edit-batch-hint">{selectedIds.size} user{selectedIds.size === 1 ? '' : 's'} selected</p>

                <label className="bulk-edit-batch-label">Field</label>
                <select className="cell-input cell-select" value={batchField} onChange={(e) => handleBatchFieldChange(e.target.value)}>
                  {batchFieldDefs.map((f) => (
                    <option key={f.key} value={f.key}>{f.label}</option>
                  ))}
                </select>

                <label className="bulk-edit-batch-label">New value</label>
                {activeBatchDef?.type === 'select' && (
                  <SelectCell
                    value={batchValue}
                    options={activeBatchDef.options}
                    pill={activeBatchDef.pill}
                    colorMap={activeBatchDef.colorMap}
                    onChange={setBatchValue}
                  />
                )}
                {activeBatchDef?.type === 'multiselect' && (
                  <MultiSelectCell
                    value={batchValue}
                    options={activeBatchDef.key === 'semesters' ? semesterFieldOptions : activeBatchDef.options}
                    onChange={setBatchValue}
                    max={activeBatchDef.max}
                  />
                )}
                {activeBatchDef?.type === 'checkbox' && (
                  <label className="bulk-edit-batch-checkbox">
                    <CheckboxCell value={batchValue} onChange={setBatchValue} />
                    Suspended
                  </label>
                )}

                <button
                  type="button"
                  className="button list-button"
                  disabled={selectedIds.size === 0 || applyingBatch}
                  onClick={requestBatchUpdate}
                >
                  {applyingBatch ? 'Applying...' : `Apply to ${selectedIds.size} Selected`}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="user-list-content-section bulk-edit-content">
          <div className="bulk-edit-toolbar">
            <span className="bulk-edit-count">
              {filteredRows.length} user{filteredRows.length === 1 ? '' : 's'} shown
              {dirtyIds.length > 0 ? ` · ${dirtyIds.length} unsaved` : ''}
            </span>
            <button type="button" className="button" disabled={dirtyIds.length === 0 || savingAll} onClick={handleSaveAll}>
              {savingAll ? 'Saving...' : `Save All (${dirtyIds.length})`}
            </button>
          </div>

          {loading && allRows.length === 0 ? (
            <LoadingSpinner message="Loading users" size="sm" />
          ) : (
            <div className="bulk-edit-table-wrapper">
              <div className="bulk-edit-table-scroll">
                <table className="bulk-edit-table">
                  <thead>
                    <tr>
                      <th className="sticky-col checkbox-col">
                        <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAllFiltered} />
                      </th>
                      <th className="sticky-col pic-col">Pic</th>
                      {fieldDefs.map((f) => (
                        <th key={f.key}>{f.label}{f.required ? ' *' : ''}</th>
                      ))}
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <UserRow
                        key={row._id}
                        row={row}
                        dirty={dirtyFields[row._id]}
                        status={rowStatus[row._id]}
                        errorMessage={rowErrors[row._id]}
                        selected={selectedIds.has(row._id)}
                        semesterOptions={semesterFieldOptions}
                        fieldDefs={fieldDefs}
                        onToggleSelect={() => toggleSelectRow(row._id)}
                        onFieldChange={handleFieldChange}
                        onSave={() => performSaveRow(row._id)}
                        onCancel={() => handleCancelRow(row._id)}
                      />
                    ))}
                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={fieldDefs.length + 3} className="bulk-edit-empty">No users match your search criteria</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmBatch && (
        <CustomAlertWithButtons
          title="Apply batch update?"
          type="warning"
          message={`This will set "${activeBatchDef?.label}" for ${selectedIds.size} selected user(s).`}
          button1Text="Cancel"
          button2Text="Apply"
          onButton1Click={() => setConfirmBatch(false)}
          onButton2Click={applyBatchUpdate}
        />
      )}

      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          autoDismiss
          timeout={5000}
          onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
        />
      )}

      {error && <div className="bulk-edit-error">Error loading users: {error.message}</div>}
    </div>
  );
}

export default UsersBulkEdit;
